import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import ReviewsIcon from "@mui/icons-material/Reviews";
import SearchIcon from "@mui/icons-material/Search";
import WorkspacePremiumIcon from "@mui/icons-material/EmojiEvents";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserId } from "../../utils/authSession";
import { useHistoryRowsQuery } from "../../queries/useToeicQueries";

const columns = [
  { id: "rank", label: "Rank", minWidth: 92, align: "center" },
  { id: "name", label: "Test", minWidth: 190 },
  { id: "score", label: "Score", minWidth: 120, align: "center" },
  { id: "day", label: "Day", minWidth: 180, align: "center" },
  { id: "review", label: "Review", minWidth: 110, align: "center" },
];

const searchFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "999px",
    bgcolor: "var(--toeic-surface)",
    boxShadow: "none",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputAdornment-root": {
    width: 30,
    height: 30,
    maxHeight: 30,
    borderRadius: "999px",
    bgcolor: "#e8f6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    mt: "0 !important",
    mr: 0.75,
  },
  "& .MuiOutlinedInput-input": { py: 0, fontWeight: 500, fontSize: 14 },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

const medalStyles = [
  {
    label: "Best",
    backgroundColor: "#fff7d6",
    color: "#b77900",
    border: "#f6d365",
    shadow: "rgba(183, 121, 0, 0.18)",
  },
  {
    label: "Top 2",
    backgroundColor: "#f0ecff",
    color: "#6d4aff",
    border: "#c6b8ff",
    shadow: "rgba(109, 74, 255, 0.16)",
  },
  {
    label: "Top 3",
    backgroundColor: "#ffe8f0",
    color: "#d81b60",
    border: "#ffb8cf",
    shadow: "rgba(216, 27, 96, 0.14)",
  },
];

const getRankStyle = (index) =>
  medalStyles[index] || {
    label: `#${index + 1}`,
    backgroundColor: "#f5f8fb",
    color: "#5f6b7a",
    border: "#d7e0ea",
    shadow: "rgba(15, 23, 42, 0.08)",
  };

const getScoreTone = (score) => {
  if (score >= 900) return { label: "Elite", color: "#b77900", bg: "#fff7d6" };
  if (score >= 785) return { label: "Strong", color: "#1f9b45", bg: "#e8f9ed" };
  if (score >= 550) return { label: "Good", color: "#6d4aff", bg: "#f0ecff" };
  return { label: "Practice", color: "#5f6b7a", bg: "#f5f8fb" };
};

export default function History() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [keySearch, setKeySearch] = useState("");
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const userID = getUserId(currentUser);
  const { data: historyData = [] } = useHistoryRowsQuery(userID);

  const filteredHistory = useMemo(() => {
    const keyword = keySearch.trim().toLowerCase();
    if (!keyword) return historyData;

    return historyData.filter((history) =>
      history.name.toLowerCase().includes(keyword)
    );
  }, [historyData, keySearch]);

  const rankedHistory = useMemo(
    () => [...filteredHistory].sort((a, b) => b.score - a.score),
    [filteredHistory]
  );

  const topAttempts = rankedHistory.slice(0, 3);
  const bestScore = rankedHistory[0]?.score || 0;
  const averageScore = filteredHistory.length
    ? Math.round(
        filteredHistory.reduce((total, history) => total + history.score, 0) /
          filteredHistory.length
      )
    : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSearchChange = (e) => {
    setKeySearch(e.target.value);
    setPage(0);
  };

  return (
    <Box
      sx={{
        mt: 4,
        mb: 6,
        width: "100%",
      }}
    >
      <Box component="header">
        <Box className="toeic-reveal">
        <Typography
          component="h1"
          sx={{
            m: 0,
            color: "var(--toeic-ink)",
            fontSize: { xs: 36, md: 56 },
            lineHeight: { xs: 1.05, md: 1 },
            fontWeight: 800,
            letterSpacing: 0,
          }}
        >
          <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                position: "absolute",
                left: "-0.12em",
                right: "-0.12em",
                bottom: "0.04em",
                height: "0.44em",
                bgcolor: "rgba(240, 184, 77, 0.34)",
                borderRadius: "0.45em 0.6em 0.4em 0.55em",
                transform: "rotate(-1deg)",
              }}
            />
            <Box component="span" sx={{ position: "relative" }}>
              Trial History
            </Box>
          </Box>
          .
        </Typography>
        </Box>
        <Typography
          className="toeic-reveal"
          sx={{
            mt: 2,
            maxWidth: 680,
            color: "var(--toeic-ink-soft)",
            fontSize: 18,
            lineHeight: 1.65,
            fontWeight: 700,
          }}
        >
          Track your attempts, best scores, and review every test.
        </Typography>
      </Box>

      {currentUser ? (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(3, minmax(0, 1fr))",
              gap: 2,
              my: 4,
            }}
          >
            <Box aria-hidden="true" sx={{ gridColumn: "1 / -1", borderTop: "1.5px dotted var(--toeic-line)", mb: 2 }} />
            {[
              { label: "Best score", value: bestScore, icon: EmojiEventsIcon, bg: "#fff7d6", color: "#b77900", border: "#f6d365" },
              { label: "Average", value: averageScore, icon: LeaderboardIcon, bg: "#e8f9ed", color: "#1f9b45", border: "#a9eabc" },
              {
                label: "Attempts",
                value: filteredHistory.length,
                icon: WorkspacePremiumIcon,
                bg: "#f0ecff",
                color: "#6d4aff",
                border: "#c6b8ff",
              },
            ].map((item) => {
              const Icon = item.icon;

              return (
                <Paper
                  key={item.label}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "14px",
                    border: 0,
                    boxShadow: "var(--toeic-shadow-card)",
                    bgcolor: "var(--toeic-surface)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "14px",
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: "#ffffff",
                      color: item.color,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 700 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: item.color, fontWeight: 700 }}>
                      {item.value}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>

          {topAttempts.length > 0 && (
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 2,
                mb: 3,
              }}
            >
              {topAttempts.map((attempt, index) => {
                const rankStyle = getRankStyle(index);
                const scoreTone = getScoreTone(attempt.score);

                return (
                  <Paper
                    key={attempt.historyID}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "14px",
                      border: 0,
                      boxShadow: "var(--toeic-shadow-card)",
                      backgroundColor: "var(--toeic-surface)",
                    }}
                  >
                    <Chip
                      icon={<EmojiEventsIcon />}
                      label={`${rankStyle.label} attempt`}
                      sx={{
                        mb: 1.5,
                        color: rankStyle.color,
                        backgroundColor: rankStyle.backgroundColor,
                        fontWeight: 700,
                        "& .MuiChip-icon": { color: rankStyle.color },
                      }}
                    />
                    <Typography
                      sx={{
                        color: "var(--toeic-ink-deep)",
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={attempt.name}
                    >
                      {attempt.name}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          color: rankStyle.color,
                          fontWeight: 700,
                          fontSize: 24,
                        }}
                      >
                        {attempt.score}
                      </Typography>
                      <Chip
                        size="small"
                        label={scoreTone.label}
                        sx={{
                          color: scoreTone.color,
                          backgroundColor: scoreTone.bg,
                          fontWeight: 800,
                        }}
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}

          <Box sx={{ mt: 4, mb: 3, display: "grid", gap: 4 }}>
            <Box aria-hidden="true" sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
            <Box
              className="toeic-reveal"
              sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: "18px",
                border: "1px solid rgba(215, 234, 245, 0.82)",
                bgcolor: "var(--toeic-paper-grid)",
                boxShadow: "var(--toeic-shadow-card)",
                display: "grid",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography
                  sx={{
                    color: "var(--toeic-ink)",
                    fontSize: 14,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: ".12em",
                  }}
                >
                  History Filter
                </Typography>
                <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 700 }}>
                  {filteredHistory.length} attempts
                </Typography>
              </Box>
              <TextField
                label="Search Test"
                variant="outlined"
                fullWidth
                value={keySearch}
                sx={searchFieldSx}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "var(--toeic-sky-deep)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box aria-hidden="true" sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
          </Box>

          <Paper
            sx={{
              width: "100%",
              overflow: "hidden",
              mt: 3,
              borderRadius: "14px",
              border: 0,
              boxShadow: "var(--toeic-shadow-card)",
            }}
          >
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader aria-label="history table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          minWidth: column.minWidth,
                          color: "#ffffff",
                          fontWeight: 700,
                          backgroundColor: "var(--toeic-sky)",
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rankedHistory
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const absoluteRank = page * rowsPerPage + index;
                      const rankStyle = getRankStyle(absoluteRank);
                      const scoreTone = getScoreTone(row.score);

                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row.historyID}
                          sx={{
                            backgroundColor:
                              absoluteRank < 3
                                ? rankStyle.backgroundColor
                                : absoluteRank % 2 === 0
                                ? "#f5f8fb"
                                : "#ffffff",
                          }}
                        >
                          <TableCell align="center">
                            <Chip
                              size="small"
                              icon={
                                absoluteRank < 3 ? (
                                  <EmojiEventsIcon />
                                ) : (
                                  <WorkspacePremiumIcon />
                                )
                              }
                              label={`#${absoluteRank + 1}`}
                              sx={{
                                minWidth: 62,
                                color: rankStyle.color,
                                backgroundColor: rankStyle.backgroundColor,
                                fontWeight: 700,
                                "& .MuiChip-icon": { color: rankStyle.color },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 800, color: "var(--toeic-ink-deep)" }}>
                            {row.name}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${row.score} pts`}
                              sx={{
                                color: scoreTone.color,
                                backgroundColor: scoreTone.bg,
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">{row.day}</TableCell>
                          <TableCell align="center">
                            <Button
                              component={Link}
                              to={`/review/${row.historyID}/${row.answerID}`}
                              size="small"
                              variant="contained"
                              startIcon={<ReviewsIcon />}
                              sx={{
                                fontWeight: 800,
                                textTransform: "none",
                                boxShadow: "none",
                              }}
                            >
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={rankedHistory.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      ) : (
        <Typography
          variant={isMobile ? "h6" : "h4"}
          sx={{
            p: 4,
            textAlign: "center",
            color: "var(--toeic-ink-soft)",
            borderRadius: "18px",
            backgroundColor: "var(--toeic-surface)",
            border: "1.5px dashed var(--toeic-line)",
          }}
        >
          Please login to see your history
        </Typography>
      )}
    </Box>
  );
}
